/*
 * Authors: mariushirjanu
 * Created: 07/12/2022
 */

import * as _ from 'underscore';
import { util } from '../../../../shared/util';
import { AfterViewInit, Component, Input } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { IOrderContext } from '../order-entry-data.service';
import { OrderEntryRequesterService } from '../requester/order-entry-requester.service';
import { IContact, ICountry, INamedObject, IQuestion } from '../../../../../type_definitions/datatypes';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { IRequester, OrderEntryRequesterDataService } from '../requester/order-entry-requester-data.service';
import { PriceCartService } from '../../cart/price-cart.service';
import { BuyingGuideModel } from '../../catalog/guide/buying-guide.model';
import { OrderCatalogService } from '../../catalog/order-catalog.service';
import { SurveyDataService } from '../../../../shared/survey/survey-data.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { GaValidateService, IValidatingComponent } from '../../../../shared/formly/ga-validate.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { OrderEntryWebshopService } from './order-entry-webshop.service';
import {
  EVENT_ORDER_BP_LIST_UPDATED,
  EVENT_ORDER_CONTEXT_UPDATED,
  EVENT_ORDER_COUNTRY_LIST_UPDATED,
  EVENT_ORDER_COUNTRY_SELECTION_UPDATED,
  EVENT_ORDER_LOADING_BPS,
  EVENT_ORDER_LOADING_COUNTRIES,
  EVENT_ORDER_LOADING_CONTEXT,
  EVENT_ORDER_WEBSHOP_LIST_UPDATED,
  EVENT_ORDER_WEBSHOP_SELECTION_UPDATED
} from '../../../../shared/app-events';
import { STATE_ORDERENTRY_WEBSHOP } from '../../../../shared/app-states';
import { FormlyAlertComponent } from '../../../../shared/formly/components/formly-alert.component';
import { Alert } from '../../../../shared/app-styles';
import { WIZARD_ORDER } from '../../../../shared/app-wizards';
import { GaWizardService } from '../../../../shared/wizard/ga-wizard.service';
import { GaWizard } from '../../../../shared/wizard/ga-wizard';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { OrderEntryStepId } from '../order-entry-steps';
import { OrderCatalogDataService } from '../../catalog/order-catalog-data.service';

interface IModel {
  shop:INamedObject;
  country:INamedObject;
  bp:INamedObject;
  requester:IContact;
  testMode:boolean;
}

@Component({
  selector: 'order-entry-webshop',
  template: `
    <h2 class="mt-0" translate>ORDERENTRY.WEBSHOP.SELECTWEBSHOP</h2>

    <formly [form]="form" [fields]="webshopFormFields" [model]="model" [noValidate]="true" class="webshop-country-chooser form-horizontal"></formly>
    
    <div class="row">
      <div class="col-md-4" *ngIf="addContactPermission">
        <span translate>ORDERENTRY.WEBSHOP.FIND</span> <a href="#" translate>ORDERENTRY.WEBSHOP.ADD_CONTACT</a>
      </div>
    </div>

    <h2 *ngIf="requesterRequired && (reqModel.contact || reqModel.address)" translate>
      ORDERENTRY.WEBSHOP.REQUESTER_INFO
    </h2>
    <div class="row requester-contact" *ngIf="reqModel.contact && requesterRequired">
      <div class="col-md-6">
        <div *ngIf="reqModel.contact.fullName">{{reqModel.contact.fullName}}</div>
        <div *ngIf="reqModel.contact.email"><a href="mailto:{{reqModel.contact.email}}">{{reqModel.contact.email}}</a></div>
        <div *ngIf="reqModel.contact.directPhone">{{reqModel.contact.directPhone}}</div>
      </div>
      <div class="col-md-2" *ngIf="editContactPermission">
        <button class="btn btn-default" translate>ORDERENTRY.WEBSHOP.EDIT_CONTACT</button>
      </div>
    </div>
    
    <div class="row requester-address" *ngIf="requesterRequired && reqModel.address">
      <h3 class="col-md-8" *ngIf="reqModel.address.description">
        {{reqModel.address.description}}
      </h3>
      <div class="col-md-8">
        <location-info [location]="reqModel.address.location" [vatName]="reqModel.address.vat.name"></location-info>
      </div>
    </div>
    
    <h2 *ngIf="loadingCtx">
      <activity-indicator></activity-indicator>
      <span translate>ORDERENTRY.WEBSHOP.LOADING</span>
    </h2>
    
    <div *ngIf="!loadingCtx && ctx">
      <shipping-info [shopContext]="ctx"></shipping-info>
      <survey-questions [title]="countryInfoTitle" [questions]="countryInfoQuestions" onlyAnswered="true"></survey-questions>
    </div>
  `
})
export class OrderEntryWebshopComponent implements IValidatingComponent, AfterViewInit {
  @Input() public orderId:number;

  public addContactPermission:boolean = false;
  public editContactPermission:boolean = false;
  public ctx:IOrderContext;
  public loadingCtx:boolean;
  public reqModel:OrderEntryRequesterService;
  public model:IModel;
  public requesterRequired:boolean;
  public webshopFormFields:IExtraFieldConfigurationObject[];
  public countryInfoQuestions:IQuestion[] = [];
  public countryInfoTitle:string;
  public form:UntypedFormGroup = new UntypedFormGroup({});

  private shopField:IExtraFieldConfigurationObject;
  private countryField:IExtraFieldConfigurationObject;
  private bpField:IExtraFieldConfigurationObject;
  private testModeField:IExtraFieldConfigurationObject;
  private bpOutdatedField:IExtraFieldConfigurationObject;
  private requesterSearchField:IExtraFieldConfigurationObject;
  private bpOutdatedWarningText:string;
  private eventHandler:Subscription;
  private wizard:GaWizard;
  private onValidationReadyDeferred:IGADeferred<void>; // resolved when the component is ready for validation

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private orderEntryRequesterDataService:OrderEntryRequesterDataService,
    private priceCartService:PriceCartService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private validateService:GaValidateService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private buyingGuideModel:BuyingGuideModel,
    private orderCatalogService:OrderCatalogService,
    private surveyDataService:SurveyDataService,
    private translationService:TranslationService,
    private gaEventService:GaEventService,
    private stateService:StateService,
    private wizardService:GaWizardService,
    private orderCatalogDataService:OrderCatalogDataService,
    deferredService:GaDeferredService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, OrderEntryStepId.WEBSHOP);
    this.reqModel = this.orderEntryRequesterService;
    this.countryInfoTitle = this.translationService.instant('ORDERENTRY.WEBSHOP.TITLE');
    this.bpOutdatedWarningText = this.translationService.instant('ORDERENTRY.WEBSHOP.SHIPPED_FROM_WARNING');

    this.webshopFormFields = this.createFields();
    if(this.orderId){
      // for existing orders this is necessary for the first time as the first EVENT_ORDER_LOADING_CONTEXT won't be received because listener is not yet initialised
      this.loadingCtx = true;

      // when coming from the order overview page, ensure that all field options are reset to their default state.
      this.clearFieldOptions();
    } else {
      this.fillFields();
    }
    this.initModel();
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private listen():void {
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      // after a select is changed, wait to receive list change events -> update the list and remove spinner
      switch(event.name){
        // events when dropdown list change

        case EVENT_ORDER_WEBSHOP_LIST_UPDATED:
          this.formlyService.fillField(this.shopField, event.data, true);
          break;

        case EVENT_ORDER_COUNTRY_LIST_UPDATED:
          this.formlyService.fillField(this.countryField, event.data, true);
          break;

        case EVENT_ORDER_BP_LIST_UPDATED: {
          const newBPs:INamedObject[] = util.deepClone(event.data);

          if (this.orderEntryWebshopService.selectedBP && !_.findWhere(newBPs, { id: this.model.bp.id })) {
            newBPs.push(this.model.bp);
            this.orderEntryWebshopService.bpOutdated = this.model.bp.id;
          }

          this.formlyService.fillField(this.bpField, newBPs, true);
          break;
        }

        // context event
        case EVENT_ORDER_CONTEXT_UPDATED:
          this.shopContext = event.data;
          break;


        // service to model events

        case EVENT_ORDER_WEBSHOP_SELECTION_UPDATED:
          this.shopField.formControl.patchValue(event.data);
          break;

        case EVENT_ORDER_COUNTRY_SELECTION_UPDATED:
          this.countryField.formControl.patchValue(event.data);
          break;


        // loading events

        case EVENT_ORDER_LOADING_COUNTRIES:
          this.formlyService.setFieldStatus(this.countryField, event.data);
          break;

        case EVENT_ORDER_LOADING_BPS:
          this.formlyService.setFieldStatus(this.bpField, event.data);
          break;

        case EVENT_ORDER_LOADING_CONTEXT:
          this.loadingCtx = event.data;
          break;
      }
    });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const wrapperClass:string = 'col-md-8';

    this.bpOutdatedField = this.formlyService.aCustom()
      .withComponent(FormlyAlertComponent)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            message: this.bpOutdatedWarningText,
            alertClass: Alert.WARNING
          }
        }
      })
      .withHideExpression(() => !this.orderEntryWebshopService.bpOutdated)
      .withWrapperClass('col-md-12')
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .build();

    // webshop fields
    this.shopField = this.formlyService.aSelect('shop', 'ORDERENTRY.WEBSHOP.LABELS.WEBSHOP')
      .isRequired()
      .withFormat(this.formattingService.formatCompany)
      .withWrapperClass(wrapperClass)
      .withoutError()
      .onChange(this.onShopChange)
      .build();

    this.countryField = this.formlyService.aSelect('country', 'ORDERENTRY.WEBSHOP.LABELS.COUNTRY')
      .isRequired()
      .isDisabled()
      .withFormat(this.formattingService.formatCountry)
      .withWrapperClass(wrapperClass)
      .withoutError()
      .withExtraOptions({
        templateOptions: {
          addInitialOption: false
        }
      })
      .onChange(this.onCountryChange)
      .build();

    this.bpField = this.formlyService.aSelect('bp', 'ORDERENTRY.WEBSHOP.LABELS.SHIPPED_FROM')
      .isRequired()
      .isDisabled()
      .withFormat(this.formattingService.formatCompany)
      .withWrapperClass(wrapperClass)
      .withoutError()
      .withExtraOptions({
        templateOptions: {
          addInitialOption: false
        }
      })
      .onChange(this.onBPChange)
      .build();

    this.requesterSearchField = this.formlyService.aSearch('requester', 'ORDERENTRY.WEBSHOP.LABELS.REQUESTER')
      .isRequired()
      .isDisabled()
      .isHide()
      .withFormat(this.formattingService.formatContactFN)
      .withWrapperClass(wrapperClass)
      .withExtraOptions({
        templateOptions: {
          minSearchLength: 1
        }
      })
      .onChange(this.onRequesterChange)
      .build();

    const fields:IExtraFieldConfigurationObject[] = [
      this.bpOutdatedField,
      this.shopField,
      this.countryField,
      this.bpField,
      this.requesterSearchField
    ];

    // test mode
    //hidden for punchout users and existing orders
    if (this.stateService.is(STATE_ORDERENTRY_WEBSHOP) && !this.orderId) {
      this.testModeField = this.formlyService.aCheckbox('testMode', 'ORDERENTRY.WEBSHOP.LABELS.TEST')
        .withWrapperClass(wrapperClass)
        .onChange(this.onTestModeChange)
        .build();
      fields.push(this.testModeField);
    }

    return fields;
  }

  private initModel():void {
    this.model = {
      shop: this.orderEntryWebshopService.selectedShop,
      country: this.orderEntryWebshopService.selectedCountry,
      bp: this.orderEntryWebshopService.selectedBP,
      requester: this.orderEntryRequesterService.contact,
      testMode: this.orderEntryWebshopService.testMode
    };

    this.shopContext = this.orderEntryWebshopService.shopContext;
    this.loadingCtx = this.orderEntryWebshopService.loadingContext; // the component isn't initialized when receiving the first EVENT_ORDER_LOADING_CONTEXT event, so set it here

    // for an existing order the values are preloaded, as well for a new order when returning to the tab
    if(this.stateService.is(STATE_ORDERENTRY_WEBSHOP)) {
      if (this.model.country) {
        this.onCountryChange(this.model.country);
        this.formlyService.setFieldStatus(this.countryField, false);
      }

      if (this.model.bp) {
        this.onBPChange(this.model.bp);
        this.formlyService.setFieldStatus(this.bpField, false);
      }

      if (this.model.requester) {
        this.requesterSearchField.hide = false;
        this.setRequesterUrl(this.orderEntryWebshopService.selectedShop.id);
        this.onRequesterChange(this.model.requester);
        this.formlyService.setFieldStatus(this.requesterSearchField, false);
      }
    }
  }

  private fillFields():void {
    this.formlyService.fillField(this.shopField, this.orderEntryWebshopService.possibleShops);
    if(this.orderEntryWebshopService.possibleCountries){
      this.formlyService.fillField(this.countryField, this.orderEntryWebshopService.possibleCountries);
    }
    if(this.orderEntryWebshopService.possibleBPs){
      this.formlyService.fillField(this.bpField, this.orderEntryWebshopService.possibleBPs);
    }
  }

  private clearFieldOptions():void {
    this.shopField.templateOptions.options = [];
    this.countryField.templateOptions.options = [];
    this.bpField.templateOptions.options = [];
  }

  private setRequesterUrl(shopId:number):void {
    this.requesterSearchField.templateOptions.disabled = false;
    this.requesterSearchField.templateOptions.url = `rest/orderentry/webshops/${shopId}/requesters`;
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };
  
  public validate = ():Promise<void | string[]> => {
    const extraErrors:string[] = [];
    if (this.orderEntryWebshopService.bpOutdated) {
      extraErrors.push(this.bpOutdatedWarningText);
    }

    return this.validateService.validateFields(this.webshopFormFields, this.model, true, extraErrors);
  };

  private setSelectedBP(bp?:INamedObject):void {
    this.orderEntryWebshopService.selectedBP = bp;
    this.orderCatalogService.companyCountryId = bp?.id;
  }

  private loadCountryInfo():void {
    const countryId:number = this.orderEntryWebshopService.selectedCountry.id;

    this.surveyDataService.getSurveyQuestions(countryId, SurveyDataService.WORKFLOW_COUNTRY)
      .then((questions:IQuestion[]) => {
        this.countryInfoQuestions = questions;
      });
  }

  private onShopChange = (newShop:INamedObject):void => {
    this.formlyService.setFieldStatus(this.countryField, undefined, undefined, !newShop);
    this.clearRequester();
    if (newShop) {
      // we only want to take action if the change hasn't been caused by a change in this.orderEntryWebshopService.selectedShop
      // we don't want to empty the options on first run. e.g.: when navigating back to webshop page
      if (!this.orderEntryWebshopService.selectedShop || (this.orderEntryWebshopService.selectedShop !== newShop)) {
        this.countryField.formControl.patchValue(undefined);
        this.countryField.templateOptions.options = [];

        this.bpField.formControl.patchValue(undefined);
        this.bpField.templateOptions.options = [];

        this.orderEntryWebshopService.selectedShop = newShop;
        this.orderEntryWebshopService.selectedCountry = undefined;
        this.buyingGuideModel.clear();
        this.setSelectedBP();
        this.priceCartService.cartStatus = [];
      }

      if (this.requesterRequired && this.requesterSearchField) {
        this.setRequesterUrl(newShop.id);
      }
    }
  };

  private onCountryChange = (newCountry:ICountry):void => {
    this.formlyService.setFieldStatus(this.bpField, undefined, undefined, !newCountry);

    if (newCountry) {

      // we only want to take action if the change hasn't been caused by a change in orderEntryWebshopService.selectedCountry
      // we don't want to empty the options on first run. e.g.: when navigating back to webshop page
      if (!this.orderEntryWebshopService.selectedCountry || (this.orderEntryWebshopService.selectedCountry !== newCountry)) {
        this.bpField.formControl.patchValue(undefined);
        this.bpField.templateOptions.options = [];
        this.orderEntryWebshopService.selectedCountry = newCountry;
        this.buyingGuideModel.clear();
        this.orderCatalogDataService.clear(); // clear filter to re-fetch them when changing the country
        this.setSelectedBP();
        this.priceCartService.cartStatus = [];
      }
    }

    if (this.model.country) {
      this.loadCountryInfo();
    }
  };

  private onBPChange = (newBP:INamedObject):void => {
    if (newBP) {
      // removes outdated BP from bp-list when another bp is selected
      if (this.orderEntryWebshopService.bpOutdated && newBP.id !== this.orderEntryWebshopService.bpOutdated) {
        this.bpField.templateOptions.options = _.reject(this.bpField.templateOptions.options, (option:any) => {
          return option.id === this.orderEntryWebshopService.bpOutdated;
        });
        this.orderEntryWebshopService.bpOutdated = null;
      }
      this.setSelectedBP(newBP);
    }
  };
  
  private onRequesterChange = (requester:IContact) => {
    if (requester) {
      const contact:IContact = util.deepClone(requester);

      if(requester.id !== this.reqModel?.contact?.id) {
        this.orderEntryRequesterDataService.requester(requester.id)
          .then((completeRequester:IRequester) => {
            contact.email = completeRequester.email;
            contact.directPhone = completeRequester.directPhone;

            this.reqModel.address = completeRequester.address;
            this.reqModel.contact = contact;
          });
      }
    } else {
      this.reqModel.contact = undefined;
      this.reqModel.address = undefined;
    }
  };
  
  private onTestModeChange = (testMode:boolean):void => {
    this.orderEntryWebshopService.testMode = testMode;
  };

  private clearRequester():void {
    if (this.requesterRequired && this.requesterSearchField) {
      this.requesterSearchField.formControl.patchValue(undefined);
      this.requesterSearchField.formControl.markAsPristine();
    }
  }

  private set shopContext(context:IOrderContext){
    this.ctx = context;

    if (this.ctx) {
      this.requesterRequired = this.ctx.requesterAllowed;
    } else {
      this.requesterRequired = null;
    }

    this.requesterSearchField.hide = !this.requesterRequired;
    if (this.requesterRequired && this.orderEntryWebshopService.selectedShop?.id) {
      this.setRequesterUrl(this.orderEntryWebshopService.selectedShop.id);
    }

    // mark order in test mode when if webshop is set in test mode
    if(context?.isTestOrder && this.testModeField){
      this.testModeField.formControl?.patchValue(context.isTestOrder);
    }
  }

  public ngOnDestroy():void {
    this.eventHandler?.unsubscribe();
    this.wizard.clearCurrentValidator();
  }
}